import { useState } from "react";
import { Field, Form } from "react-final-form";
import { connect } from "react-redux";
import md5 from "md5";
import getConfig from "next/config";

import { addAlert, apiCall, removeModal } from "@store/app/actions";
import InputText from "@components/Form/InputText";
import Textarea from "@components/Form/Textarea";
import Modal from "@components/Modal";
import {
  composeFormValidators,
  isRequired,
  hasMinLength,
  hasMaxLength,
} from "@lib/validations";
import Button from "@components/Button";
import { editCustomerReviewApi } from "@lib/api";
import EditReviewModalImages from "@containers/Reviews/EditReviewModal/EditReviewModalImages";
import EditReviewModalRating from "@containers/Reviews/EditReviewModal/EditReviewModalRating";
import EditReviewModalTerms from "@containers/Reviews/EditReviewModal/EditReviewModalTerms";

const {
  publicRuntimeConfig: {
    settings: { cdn },
  },
} = getConfig();

const EditReviewModal = ({
  modalId,
  review,
  removeModal,
  addAlert,
  apiCall,
  onEditReview,
}) => {
  const [newImages, setNewImages] = useState([]);
  const [deleteExistingImages, setDeleteExistingImages] = useState([]);

  const thumbnailImages = review?.images?.map(
    (image) =>
      `${cdn}/resize/reviews-files/${review._id}/${md5("60x60")}/${image}`
  );

  const parseNewImages = (imgArr) => {
    setNewImages([]);

    imgArr.map((item) => {
      const reader = new FileReader();
      reader.readAsDataURL(item.file);
      reader.addEventListener("load", () =>
        setNewImages((prevState) => [...prevState, reader.result])
      );
    });
  };

  const removeExistingImages = (img) => {
    const splitUrl = img.split("/");
    const imgUrl = splitUrl[splitUrl.length - 1];

    setDeleteExistingImages((prevState) => [...prevState, imgUrl]);
  };

  const clearModalState = () => {
    setNewImages([]);
    setDeleteExistingImages([]);
    removeModal();
  };

  const submitForm = async (data) => {
    const imagesPayload = {
      images: newImages,
      deleteImages: deleteExistingImages,
    };

    delete data.gdpr;
    delete data.termsAndContitions;

    const apiArgs = [{ ...data, ...imagesPayload }, review._id];

    const response = await apiCall({
      apiArgs,
      apiCall: editCustomerReviewApi,
      formatResponse: (response) => response,
      spinner: true,
      returnResponse: true,
      postSuccess: () => {
        addAlert({
          variant: "success",
          message: "Am trimis recenzia spre aprobare. Multumim!",
        });
      },
      preFail: (response) => {
        addAlert({
          message:
            response.status === 409
              ? response.data.message
              : "Ne pare rau, a aparut o eroare. Te rugam sa reincerci salvarea recenziei.",
          variant: "error",
        });
      },
    });

    if (response && response.status === 200) {
      onEditReview();
      removeModal(modalId);
      clearModalState();
    }
  };

  return (
    <Modal
      id={modalId}
      header="Editeaza recenzia"
      onRemoveModal={clearModalState}
    >
      <Form
        onSubmit={submitForm}
        initialValues={{
          rating: review.rating || 0,
          title: review.title || "",
          body: review.body || "",
        }}
        render={({ handleSubmit, values }) => {
          return (
            <form onSubmit={handleSubmit} noValidate={true}>
              <Field
                label="Titlul recenziei"
                name="title"
                id="review_title"
                required
                component={InputText}
                validate={composeFormValidators(
                  isRequired(),
                  hasMinLength(5),
                  hasMaxLength(150)
                )}
              />

              <EditReviewModalRating values={values} />

              <div className="mt-6">
                <div>
                  <label
                    htmlFor="body"
                    className="Form-label is-required inline-block"
                  >
                    Descriere
                  </label>

                  <span className="inline-block mb-1 ml-1 text-gray-light font-normal text-sm leading-4">
                    (30 — 3000 de caractere)
                  </span>
                </div>

                <Field
                  name="body"
                  id="review_body"
                  component={Textarea}
                  inputClassName="max-w-fulli"
                  rows="6"
                  validate={composeFormValidators(
                    isRequired(),
                    hasMinLength(30),
                    hasMaxLength(3000)
                  )}
                />
              </div>

              <div className="mt-6">
                <label className="mb-3">Imagini produs</label>
                <EditReviewModalImages
                  parseNewImages={parseNewImages}
                  removeExistingImages={removeExistingImages}
                  thumbnailImages={thumbnailImages}
                />
              </div>

              <div className="mt-6">
                <EditReviewModalTerms />
              </div>

              <div className="text-center md:flex mt-6">
                <Button
                  type="submit"
                  title="Salveaza"
                  variant="primary"
                  classesProp="mb-4 md:mb-0 w-full md:w-[240px] my-auto"
                />

                <p
                  onClick={() => clearModalState()}
                  className="text-red-brand underline my-auto px-8 cursor-pointer"
                >
                  Renunta
                </p>
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default connect(null, { addAlert, removeModal, apiCall })(
  EditReviewModal
);
